import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  Suspense,
} from "react";
import { useOktaAuth } from "@okta/okta-react";
import { User } from "../models/User";
import { UserService } from "../services/UserService";
import UserErrorBoundary from "../components/error-boundaries/UserErrorBoundary";
import LoadingFallback from "../components/loaders/LoadingFallback";
import {
  mapPersonaId,
  mapAccessLevelId,
  shouldUpdateUserFromOkta,
  hasGroup,
  getPersonaDisplayName,
  getAccessLevelDisplayName,
} from "../utils/userGroupUtils";
import {
  safeLocalStorageSet,
  safeLocalStorageRemove,
} from "../utils/storageUtils";

export interface UserInfo {
  email: string;
  name: string;
  given_name: string;
  family_name: string;
  preferred_username: string;
  sub?: string;
  oktaId?: string | null;
  userId?: number;
  locale?: string | null;
  zoneinfo?: string | null;
  accessLevel?: string;
  personaName?: string;
  groups?: string[];
  // Group membership flags
  isOwner?: boolean;
  isBDT?: boolean;
  isRSMorISR?: boolean;
  isRVP?: boolean;
  isRDnD?: boolean;
  isMarketing?: boolean;
  isVisitor?: boolean;
  isSalesOps?: boolean;
  // Additional group membership flags
  isAdministrator?: boolean;
  isSuperuser?: boolean;
  isAdvancedUser?: boolean;
  isStandardUser?: boolean;
  isReviewer?: boolean;
}

export interface UserContextType {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isUserLoading: boolean;
  // Group membership flags as direct states
  isOwner: boolean;
  isBDT: boolean;
  isRSMorISR: boolean;
  isRVP: boolean;
  isRDnD: boolean;
  isMarketing: boolean;
  isVisitor: boolean;
  isSalesOps: boolean;
  // Additional group membership flags
  isAdministrator: boolean;
  isSuperuser: boolean;
  isAdvancedUser: boolean;
  isStandardUser: boolean;
  isReviewer: boolean;
  // Setter functions for group membership flags
  setIsOwner: (value: boolean) => void;
  setIsBDT: (value: boolean) => void;
  setIsRSMorISR: (value: boolean) => void;
  setIsRVP: (value: boolean) => void;
  setIsRDnD: (value: boolean) => void;
  setIsMarketing: (value: boolean) => void;
  setIsVisitor: (value: boolean) => void;
  setIsSalesOps: (value: boolean) => void;
  setIsAdministrator: (value: boolean) => void;
  setIsSuperuser: (value: boolean) => void;
  setIsAdvancedUser: (value: boolean) => void;
  setIsStandardUser: (value: boolean) => void;
  setIsReviewer: (value: boolean) => void;
  // Setter functions for personaName and accessLevel
  setPersonaName: (value: string) => void;
  setAccessLevel: (value: string) => void;
  // Function to refresh user data
  refetchUser?: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  user: null,
  isAuthenticated: false,
  isUserLoading: true,
  // Default values for group membership flags
  isOwner: false,
  isBDT: false,
  isRSMorISR: false,
  isRVP: false,
  isRDnD: false,
  isMarketing: false,
  isVisitor: false,
  isSalesOps: false,
  isAdministrator: false,
  isSuperuser: false,
  isAdvancedUser: false,
  isStandardUser: false,
  isReviewer: false,
  // Default setter functions (no-op)
  setIsOwner: () => {},
  setIsBDT: () => {},
  setIsRSMorISR: () => {},
  setIsRVP: () => {},
  setIsRDnD: () => {},
  setIsMarketing: () => {},
  setIsVisitor: () => {},
  setIsSalesOps: () => {},
  setIsAdministrator: () => {},
  setIsSuperuser: () => {},
  setIsAdvancedUser: () => {},
  setIsStandardUser: () => {},
  setIsReviewer: () => {},
  // Default setter functions for personaName and accessLevel
  setPersonaName: () => {},
  setAccessLevel: () => {},
  // Default refetchUser function
  refetchUser: async () => Promise.resolve(),
});

export const useUser = () => useContext(UserContext);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const { authState, oktaAuth } = useOktaAuth();
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [contextValue, setContextValue] = useState<UserContextType>({
    user: null,
    isAuthenticated: false,
    isUserLoading: true,
    // Default values for group membership flags
    isOwner: false,
    isBDT: false,
    isRSMorISR: false,
    isRVP: false,
    isRDnD: false,
    isMarketing: false,
    isVisitor: false,
    isSalesOps: false,
    isAdministrator: false,
    isSuperuser: false,
    isAdvancedUser: false,
    isStandardUser: false,
    isReviewer: false,
    // Default setter functions (no-op)
    setIsOwner: () => {},
    setIsBDT: () => {},
    setIsRSMorISR: () => {},
    setIsRVP: () => {},
    setIsRDnD: () => {},
    setIsMarketing: () => {},
    setIsVisitor: () => {},
    setIsSalesOps: () => {},
    setIsAdministrator: () => {},
    setIsSuperuser: () => {},
    setIsAdvancedUser: () => {},
    setIsStandardUser: () => {},
    setIsReviewer: () => {},
    // Default setter functions for personaName and accessLevel
    setPersonaName: () => {},
    setAccessLevel: () => {},
    // Default refetchUser function
    refetchUser: async () => Promise.resolve(),
  });

  // Function to update group membership flags
  const updateGroupMembership = (flagName: string, value: boolean) => {
    setContextValue((prev) => {
      // Create a copy of the user object with the updated flag
      const updatedUser = prev.user
        ? {
            ...prev.user,
            [flagName]: value,
          }
        : null;

      // Store the updated user context in localStorage
      if (updatedUser) {
        safeLocalStorageSet("sfe_user_context", updatedUser);
      }

      // Return updated context with both the flag and user object updated
      return {
        ...prev,
        [flagName]: value,
        user: updatedUser,
      };
    });
  };

  // Function to update user fields like personaName and accessLevel
  const updateUserField = (fieldName: string, value: string) => {
    setContextValue((prev) => {
      // Create a copy of the user object with the updated field
      const updatedUser = prev.user
        ? {
            ...prev.user,
            [fieldName]: value,
          }
        : null;

      // Store the updated user context in localStorage
      if (updatedUser) {
        safeLocalStorageSet("sfe_user_context", updatedUser);
      }

      // Return updated context with the user object updated
      return {
        ...prev,
        user: updatedUser,
      };
    });
  };

  async function fetchUserData() {
    try {
      if (!authState) {
        setContextValue({
          user: null,
          isAuthenticated: false,
          isUserLoading: false,
          // Reset all group membership flags
          isOwner: false,
          isBDT: false,
          isRSMorISR: false,
          isRVP: false,
          isRDnD: false,
          isMarketing: false,
          isVisitor: false,
          isSalesOps: false,
          isAdministrator: false,
          isSuperuser: false,
          isAdvancedUser: false,
          isStandardUser: false,
          isReviewer: false,
          // Setter functions for group membership flags
          setIsOwner: (value: boolean) =>
            updateGroupMembership("isOwner", value),
          setIsBDT: (value: boolean) => updateGroupMembership("isBDT", value),
          setIsRSMorISR: (value: boolean) =>
            updateGroupMembership("isRSMorISR", value),
          setIsRVP: (value: boolean) => updateGroupMembership("isRVP", value),
          setIsRDnD: (value: boolean) => updateGroupMembership("isRDnD", value),
          setIsMarketing: (value: boolean) =>
            updateGroupMembership("isMarketing", value),
          setIsVisitor: (value: boolean) =>
            updateGroupMembership("isVisitor", value),
          setIsSalesOps: (value: boolean) =>
            updateGroupMembership("isSalesOps", value),
          setIsAdministrator: (value: boolean) =>
            updateGroupMembership("isAdministrator", value),
          setIsSuperuser: (value: boolean) =>
            updateGroupMembership("isSuperuser", value),
          setIsAdvancedUser: (value: boolean) =>
            updateGroupMembership("isAdvancedUser", value),
          setIsStandardUser: (value: boolean) =>
            updateGroupMembership("isStandardUser", value),
          setIsReviewer: (value: boolean) =>
            updateGroupMembership("isReviewer", value),
          // Setter functions for personaName and accessLevel
          setPersonaName: (value: string) =>
            updateUserField("personaName", value),
          setAccessLevel: (value: string) =>
            updateUserField("accessLevel", value),
          // Function to refresh user data
          refetchUser: async () => Promise.resolve(),
        });
        return;
      }

      setContextValue((prev) => ({ ...prev, isUserLoading: true }));

      // Get Okta user info
      const oktaUserInfo = await oktaAuth.getUser().catch((error) => {
        console.error("Error fetching Okta user info:", error);
        throw new Error("Failed to fetch Okta user info");
      });

      // Get user from backend if we have an Okta ID
      let backendUser: User | null = null;

      // Extract username from email (everything before @)
      const email = oktaUserInfo.email || "";
      const username = email.split("@")[0];

      // Process groups from Okta
      const userGroups = Array.isArray(oktaUserInfo.groups)
        ? oktaUserInfo.groups.map((g) => String(g))
        : oktaUserInfo.groups
        ? [String(oktaUserInfo.groups)]
        : [];

      if (oktaUserInfo.sub) {
        try {
          // First try to get user by Okta ID
          backendUser = await UserService.getUserByOktaId(oktaUserInfo.sub);

          // If user found by Okta ID, check if we need to update from Okta
          if (
            backendUser &&
            shouldUpdateUserFromOkta(backendUser, oktaUserInfo)
          ) {
            try {
              backendUser = await UserService.updateUser(backendUser.user_id, {
                okta_id: oktaUserInfo.sub || null,
                email: oktaUserInfo.email || undefined,
                given_name: oktaUserInfo.given_name || undefined,
                family_name: oktaUserInfo.family_name || undefined,
                prefered_username: oktaUserInfo.preferred_username || username,
                persona_name: mapPersonaId(userGroups),
                access_level_name: mapAccessLevelId(userGroups),
                locale: oktaUserInfo.locale || null,
                zoneinfo: oktaUserInfo.zoneinfo || null,
              });
            } catch (updateError) {
              console.error(
                "Failed to update user with Okta data:",
                updateError
              );
            }
          }
          // If no user found by Okta ID, try username
          else if (!backendUser) {
            try {
              backendUser = await UserService.getUserByUsername(username);

              // If still no user found, create new user
              if (!backendUser) {
                try {
                  backendUser = await UserService.createUser({
                    user_name: username,
                    email: oktaUserInfo.email || "",
                    given_name: oktaUserInfo.given_name || "",
                    family_name: oktaUserInfo.family_name || "",
                    prefered_username: username,
                    okta_id: oktaUserInfo.sub || null,
                    locale: oktaUserInfo.locale || null,
                    zoneinfo: oktaUserInfo.zoneinfo || null,
                    // Default values for required fields
                    persona_name: mapPersonaId(userGroups),
                    access_level: mapAccessLevelId(userGroups),
                  });
                } catch (createError) {
                  console.error("Failed to create new user:", createError);
                  // Continue with just Okta data
                }
              }
            } catch (usernameError) {
              console.warn("Could not fetch user by username:", usernameError);
              // Continue with just Okta data
            }
          }
        } catch (error) {
          console.error("Error fetching user by Okta ID:", error);
          // Continue with just Okta data
        }

        // Store user ID in localStorage for other services
        if (backendUser?.user_id) {
          safeLocalStorageSet("userId", backendUser.user_id.toString());
        }
      }

      // Determine group membership flags
      const isOwner = hasGroup(userGroups, "SFE Owner");
      const isBDT = hasGroup(userGroups, "SFE BDT");
      const isRSMorISR = hasGroup(userGroups, "SFE RSM ISR");
      const isRVP = hasGroup(userGroups, "SFE RVP");
      const isRDnD = hasGroup(userGroups, "SFE RD&D");
      const isMarketing = hasGroup(userGroups, "SFE Marketing");
      const isVisitor = hasGroup(userGroups, "SFE Visitor");
      const isSalesOps = hasGroup(userGroups, "Sales Ops");
      const isAdministrator = hasGroup(userGroups, "SFE Administrator");
      const isSuperuser = hasGroup(userGroups, "SFE Superuser");
      const isAdvancedUser = hasGroup(userGroups, "SFE Advanced User");
      const isStandardUser = hasGroup(userGroups, "SFE Standard User");
      const isReviewer = hasGroup(userGroups, "SFE Reviewer");

      // Combine data, giving preference to Okta
      const combinedUser: UserInfo = {
        // Backend user data (will be overridden by Okta data if present)
        userId: backendUser?.user_id,
        personaName: getPersonaDisplayName(userGroups),
        accessLevel: getAccessLevelDisplayName(userGroups),

        // Okta data takes precedence
        email: oktaUserInfo.email || backendUser?.email || "",
        name:
          oktaUserInfo.name ||
          `${backendUser?.given_name || ""} ${
            backendUser?.family_name || ""
          }`.trim(),
        given_name: oktaUserInfo.given_name || backendUser?.given_name || "",
        family_name: oktaUserInfo.family_name || backendUser?.family_name || "",
        preferred_username:
          username ||
          backendUser?.prefered_username ||
          backendUser?.user_name ||
          "",
        sub: oktaUserInfo.sub,
        oktaId: oktaUserInfo.sub || backendUser?.okta_id,
        locale: oktaUserInfo.locale || backendUser?.locale || undefined,
        zoneinfo: oktaUserInfo.zoneinfo || backendUser?.zoneinfo || undefined,
        groups: userGroups,

        // Group membership flags
        isOwner,
        isBDT,
        isRSMorISR,
        isRVP,
        isRDnD,
        isMarketing,
        isVisitor,
        isSalesOps,

        // Additional group membership flags
        isAdministrator,
        isSuperuser,
        isAdvancedUser,
        isStandardUser,
        isReviewer,
      };

      // Store the user context in localStorage for OpportunityService
      safeLocalStorageSet("sfe_user_context", combinedUser);

      setContextValue({
        user: combinedUser,
        isAuthenticated: true,
        isUserLoading: false,
        // Set group membership flags directly in the context
        isOwner,
        isBDT,
        isRSMorISR,
        isRVP,
        isRDnD,
        isMarketing,
        isVisitor,
        isSalesOps,
        isAdministrator,
        isSuperuser,
        isAdvancedUser,
        isStandardUser,
        isReviewer,
        // Setter functions for group membership flags
        setIsOwner: (value: boolean) => updateGroupMembership("isOwner", value),
        setIsBDT: (value: boolean) => updateGroupMembership("isBDT", value),
        setIsRSMorISR: (value: boolean) =>
          updateGroupMembership("isRSMorISR", value),
        setIsRVP: (value: boolean) => updateGroupMembership("isRVP", value),
        setIsRDnD: (value: boolean) => updateGroupMembership("isRDnD", value),
        setIsMarketing: (value: boolean) =>
          updateGroupMembership("isMarketing", value),
        setIsVisitor: (value: boolean) =>
          updateGroupMembership("isVisitor", value),
        setIsSalesOps: (value: boolean) =>
          updateGroupMembership("isSalesOps", value),
        setIsAdministrator: (value: boolean) =>
          updateGroupMembership("isAdministrator", value),
        setIsSuperuser: (value: boolean) =>
          updateGroupMembership("isSuperuser", value),
        setIsAdvancedUser: (value: boolean) =>
          updateGroupMembership("isAdvancedUser", value),
        setIsStandardUser: (value: boolean) =>
          updateGroupMembership("isStandardUser", value),
        setIsReviewer: (value: boolean) =>
          updateGroupMembership("isReviewer", value),
        // Setter functions for personaName and accessLevel
        setPersonaName: (value: string) =>
          updateUserField("personaName", value),
        setAccessLevel: (value: string) =>
          updateUserField("accessLevel", value),
        // Function to refresh user data
        refetchUser: refetchUserData,
      });
    } catch (error) {
      console.error("Error fetching user data:", error);
      setContextValue({
        user: null,
        isAuthenticated: true, // Still authenticated, just couldn't get user data
        isUserLoading: false,
        // Reset all group membership flags on error
        isOwner: false,
        isBDT: false,
        isRSMorISR: false,
        isRVP: false,
        isRDnD: false,
        isMarketing: false,
        isVisitor: false,
        isSalesOps: false,
        isAdministrator: false,
        isSuperuser: false,
        isAdvancedUser: false,
        isStandardUser: false,
        isReviewer: false,
        // Setter functions for group membership flags
        setIsOwner: (value: boolean) => updateGroupMembership("isOwner", value),
        setIsBDT: (value: boolean) => updateGroupMembership("isBDT", value),
        setIsRSMorISR: (value: boolean) =>
          updateGroupMembership("isRSMorISR", value),
        setIsRVP: (value: boolean) => updateGroupMembership("isRVP", value),
        setIsRDnD: (value: boolean) => updateGroupMembership("isRDnD", value),
        setIsMarketing: (value: boolean) =>
          updateGroupMembership("isMarketing", value),
        setIsVisitor: (value: boolean) =>
          updateGroupMembership("isVisitor", value),
        setIsSalesOps: (value: boolean) =>
          updateGroupMembership("isSalesOps", value),
        setIsAdministrator: (value: boolean) =>
          updateGroupMembership("isAdministrator", value),
        setIsSuperuser: (value: boolean) =>
          updateGroupMembership("isSuperuser", value),
        setIsAdvancedUser: (value: boolean) =>
          updateGroupMembership("isAdvancedUser", value),
        setIsStandardUser: (value: boolean) =>
          updateGroupMembership("isStandardUser", value),
        setIsReviewer: (value: boolean) =>
          updateGroupMembership("isReviewer", value),
        // Setter functions for personaName and accessLevel
        setPersonaName: (value: string) =>
          updateUserField("personaName", value),
        setAccessLevel: (value: string) =>
          updateUserField("accessLevel", value),
        // Function to refresh user data
        refetchUser: refetchUserData,
      });
    }
  }

  // Enhanced refetchUser function that forces a refresh from Okta
  const refetchUserData = async (): Promise<void> => {
    // Clear any cached user data to ensure we get fresh data
    safeLocalStorageRemove("sfe_user_data");
    safeLocalStorageRemove("sfe_user_context");

    // Force refresh of users cache if needed
    if (UserService.shouldRefreshUserData()) {
      try {
        await UserService.refreshUsers();
      } catch (error) {
        console.error("Error refreshing users cache:", error);
      }
    }

    // Fetch fresh user data from Okta and update backend
    await fetchUserData();
  };

  // Fetch user data when authentication state changes
  useEffect(() => {
    // Only fetch user data when authState is defined and data hasn't been fetched yet
    if (authState && !hasFetchedData) {
      fetchUserData();
      setHasFetchedData(true);
    }
  }, [hasFetchedData, authState]);

  return (
    <UserErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <UserContext.Provider value={contextValue}>
          {children}
        </UserContext.Provider>
      </Suspense>
    </UserErrorBoundary>
  );
};

export default UserContext;
